import { ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { MatIconRegistry } from "@angular/material/icon";
import { DomSanitizer } from "@angular/platform-browser";
import { NavigationEnd, Router } from "@angular/router";
import { AnalyticsService } from "@app/core/services/analytics.service";
import { AzureSsoService } from "@app/core/auth";
import { MsalBroadcastService, MsalService } from "@azure/msal-angular";
import { InteractionStatus } from "@azure/msal-browser";
import { Subject } from "rxjs";
import { filter, takeUntil } from "rxjs/operators";
import { environment } from "src/environments/environment.ipt.local";

@Component({
	selector: "app-root",
	templateUrl: "./app.component.html",
	styleUrls: ["./app.component.scss"],
})
export class AppComponent implements OnInit, OnDestroy {
	isIframe = false;
	sessionChangedInAnotherTab = false;
	private destroy$ = new Subject<void>();
	private readonly analyticsDebug =
		!environment.production ||
		/localhost|127\.0\.0\.1/i.test(window.location.hostname);

	constructor(
		private iconRegistry: MatIconRegistry,
		private sanitizer: DomSanitizer,
		private analytics: AnalyticsService,
		private msalService: MsalService,
		private msalBroadcastService: MsalBroadcastService,
		private router: Router,
		private azureSsoService: AzureSsoService,
		private changeDetectorRef: ChangeDetectorRef
	) {}

	ngOnInit() {
		this.isIframe = window !== window.parent && !window.opener;
		this.addCustomIcons();
		this.initAnalytics();
		this.watchNavigation();
		this.watchMsalSession();
		this.watchCrossTabSession();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private addCustomIcons(): void {
		this.registerSvgIcon(
			"p-arrow-chevron-right",
			"../assets/icons/chevron-right.svg"
		);
		this.registerSvgIcon("p-search", "../assets/icons/search.svg");
		this.registerSvgIcon("p-bell", "../assets/icons/notificaciones.svg");
		this.registerSvgIcon("p-home", "../assets/icons/home.svg");
		this.registerSvgIcon("p-permissions", "../assets/icons/permisos.svg");
		this.registerSvgIcon("p-news", "../assets/icons/comunicados.svg");
		this.registerSvgIcon("p-apps", "../assets/icons/aplicaciones.svg");
		this.registerSvgIcon("table-plus", "../assets/icons/table-plus.svg");
		this.registerSvgIcon("table-minus", "../assets/icons/table-minus.svg");
		this.registerSvgIcon("layout-columns", "../assets/icons/layout-columns.svg");
		this.registerSvgIcon("filter-plus", "../assets/icons/filter-plus.svg");
		this.registerSvgIcon("p-download", "../assets/icons/download.svg");
	}

	private registerSvgIcon(name: string, relativePath: string) {
		this.iconRegistry.addSvgIcon(
			name,
			this.sanitizer.bypassSecurityTrustResourceUrl(relativePath)
		);
	}

	private initAnalytics(): void {
		const gaId = (environment as any).NEXT_PUBLIC_GA_MEASUREMENT_ID;
		const apiSecret = (environment as any).GA_API_SECRET;
		this.analytics.init(gaId, apiSecret, this.analyticsDebug);
		this.setUserPropsFromMsal();
	}

	private watchMsalSession(): void {
		this.msalBroadcastService.inProgress$
			.pipe(
				filter((status) => status === InteractionStatus.None),
				takeUntil(this.destroy$)
			)
			.subscribe(() => this.setUserPropsFromMsal());
	}

	reloadForSessionChange(): void {
		this.azureSsoService.reloadForSessionChange();
	}

	private watchCrossTabSession(): void {
		this.azureSsoService.sessionChanged$
			.pipe(takeUntil(this.destroy$))
			.subscribe((changed) => {
				this.sessionChangedInAnotherTab = changed;
				this.changeDetectorRef.markForCheck();
			});
	}

	private setUserPropsFromMsal(): void {
		const account = this.msalService.instance.getActiveAccount();
		const claims = (account?.idTokenClaims as any) || {};
		const user_oid = claims?.oid as string | undefined;
		const tid = claims?.tid as string | undefined;
		const upn =
			(claims?.preferred_username as string | undefined) ||
			(claims?.unique_name as string | undefined);

		this.analytics.setUserProperties({ user_oid, tid, upn });
	}

	private watchNavigation(): void {
		this.router.events
			.pipe(
				filter((event) => event instanceof NavigationEnd),
				takeUntil(this.destroy$)
			)
			.subscribe((event: NavigationEnd) => {
				this.analytics.trackPageView(event.urlAfterRedirects);
			});
	}
}
